import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Cloud as CloudIcon } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import Hero from "../components/Hero";
import SectionHead from "../components/SectionHead";
import { Reveal } from "../components/Reveal";
import Counter from "../components/Counter";
import ConsultForm from "../components/ConsultForm";
import ClientMarquee from "../components/ClientMarquee";
import KeywordMarquee from "../components/KeywordMarquee";
import ServiceAccordion from "../components/ServiceAccordion";
import { getIcon } from "../lib/icons";
import { RESOURCES } from "../i18n/resources";

export default function Home() {
  const { t, dir, lang } = useLang();
  const flip = dir === "rtl" ? "scaleX(-1)" : "none";
  const blogs = RESOURCES[lang].blogs;

  return (
    <div>
      <Hero />

      <KeywordMarquee />

      {/* STATS */}
      <section className="relative -mt-2 py-12">
        <div className="rq-container grid gap-6 sm:grid-cols-3">
          {t.stats.map((s, i) => (
            <Reveal key={i} delay={i * 120}>
              <div className="rq-card p-8 text-center">
                <div className="font-display text-4xl md:text-5xl font-bold rq-gradient-text">
                  <Counter value={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-2 font-semibold text-white">{s.label}</div>
                <div className="mt-1 text-sm text-[color:var(--rq-muted)]">{s.sub}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* UNIQUE */}
      <section className="py-12 rq-light">
        <div className="rq-container">
          <SectionHead tag={t.unique.tag} title={t.unique.title} desc={t.unique.desc} />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {t.unique.cards.map((c, i) => {
              const Icon = getIcon(["Cpu", "MonitorSmartphone", "Bot", "Shield"][i]);
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="rq-card p-7 h-full">
                    <div className="grid place-items-center h-14 w-14 rounded-xl bg-[rgba(46,230,110,0.12)] text-[color:var(--rq-green)]">
                      <Icon size={26} />
                    </div>
                    <h3 className="font-display mt-5 text-lg font-semibold text-white">{c.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[color:var(--rq-muted)]">{c.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="py-12">
        <div className="rq-container grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="rq-tag">
              <span className="h-px w-6 bg-[color:var(--rq-green)] inline-block" />
              {t.whatWeDo.tag}
            </span>
            <h2 className="font-display mt-4 text-3xl md:text-4xl font-bold text-white leading-tight">
              {t.whatWeDo.title}
            </h2>
            <p className="mt-6 text-[color:var(--rq-muted)] leading-relaxed">{t.whatWeDo.desc}</p>
            <Link to="/services" className="mt-8 inline-flex items-center gap-2 rq-glow-btn px-6 py-3">
              {t.servicesSection.cta}
              <ArrowRight size={16} style={{ transform: flip }} />
            </Link>
          </Reveal>
          <Reveal delay={150}>
            <div className="relative">
              <div className="rq-card overflow-hidden">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/8a50a129-5a0d-4560-b385-7d9dcdaad4e7/images/d454e5d6563dc25072236c027bf134c3f4fe5cf86967e8cd6844fe530babe9a0.jpeg"
                  alt="tech glass abstract"
                  className="w-full h-[360px] object-cover opacity-90"
                />
              </div>
              <div className="absolute -bottom-6 ltr:-left-6 rtl:-right-6 rq-card p-5 bg-white shadow-[0_20px_50px_-24px_rgba(14,122,58,0.5)]">
                <div className="font-display text-2xl font-bold rq-gradient-text">50+</div>
                <div className="text-xs text-[color:var(--rq-muted)]">{t.stats[2].label}</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-12 rq-light">
        <div className="rq-container">
          <SectionHead tag={t.servicesSection.tag} title={t.servicesSection.title} />
          <ServiceAccordion />
        </div>
      </section>

      {/* CLOUD banner */}
      <section className="py-10">
        <div className="rq-container">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-[rgba(46,230,110,0.2)] p-10 md:p-14">
              <div className="absolute inset-0 rq-grid-bg opacity-40" />
              <div
                className="absolute -right-20 -top-20 h-64 w-64 rounded-full blur-[100px] opacity-30"
                style={{ background: "radial-gradient(circle,#2ee66e,transparent)" }}
              />
              <div className="relative grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="rq-tag">{t.cloud.tag}</span>
                  <h2 className="font-display mt-3 text-3xl md:text-4xl font-bold text-white">{t.cloud.title}</h2>
                  <p className="mt-5 text-[color:var(--rq-muted)] leading-relaxed max-w-xl">{t.cloud.desc}</p>
                  <Link to="/cloud-data-center" className="mt-7 inline-flex items-center gap-2 rq-ghost-btn px-6 py-3">
                    {t.cloud.cta}
                    <ArrowRight size={16} style={{ transform: flip }} />
                  </Link>
                </div>
                <div className="flex justify-center">
                  <div className="rq-float grid place-items-center h-40 w-40 rounded-3xl bg-[rgba(46,230,110,0.08)] border border-[rgba(46,230,110,0.3)] text-[color:var(--rq-green)]">
                    <CloudIcon size={64} />
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PLATFORMS */}
      <section className="py-12 rq-light">
        <div className="rq-container">
          <SectionHead tag={t.platforms.tag} title={t.platforms.title} />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {t.platforms.items.map((p, i) => {
              const inner = (
                <>
                  <div className="h-14 w-14 rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-lg">
                    <img src={p.logo} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
                  </div>
                  <div className="font-display mt-4 text-lg font-bold text-[color:var(--rq-green)]">{p.name}</div>
                  <p className="mt-2 text-sm leading-relaxed text-[color:var(--rq-muted)]">{p.desc}</p>
                </>
              );
              return (
                <Reveal key={i} delay={(i % 4) * 80}>
                  {p.slug ? (
                    <Link to={`/platforms/${p.slug}`} className="rq-card p-7 h-full block group hover:border-[rgba(14,122,58,0.4)]">{inner}</Link>
                  ) : (
                    <div className="rq-card p-7 h-full">{inner}</div>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-12">
        <div className="rq-container">
          <SectionHead tag={t.partnersSection.tag} title={t.partnersSection.title} desc={t.partnersSection.desc} />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {t.partnersSection.list.map((p, i) => (
              <Reveal key={i} delay={(i % 3) * 100}>
                <div className="rq-card p-7 h-full" data-testid={`partner-card-${i}`}>
                  <div className="h-14 flex items-center">
                    <img src={p.logo} alt={p.name} loading="lazy" className="max-h-12 max-w-[170px] object-contain" />
                  </div>
                  <h3 className="font-display mt-5 text-lg font-semibold text-white">{p.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[color:var(--rq-muted)]">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BLOGS */}
      <section className="py-12 rq-light">
        <div className="rq-container">
          <SectionHead tag={t.blogsSection.tag} title={t.blogsSection.title} />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {blogs.map((b) => (
              <Reveal key={b.slug} delay={0}>
                <Link to={`/blog/${b.slug}`} className="rq-card overflow-hidden block group h-full">
                  <div className="h-52 overflow-hidden">
                    <img src={b.image} alt={b.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-semibold text-[color:var(--rq-green)]">{b.category} • {b.date}</span>
                    <h3 className="font-display mt-3 text-lg font-semibold text-white leading-snug">{b.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[color:var(--rq-muted)] line-clamp-3">{b.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOMERS */}
      <section className="py-12">
        <div className="rq-container">
          <SectionHead tag={t.customersSection.tag} title={t.customersSection.title} desc={t.customersSection.desc} />
        </div>
        <div className="mt-12 space-y-4">
          <ClientMarquee items={t.customers.slice(0, 8)} />
          <ClientMarquee items={t.customers.slice(8)} reverse />
        </div>
      </section>

      {/* CTA + FORM */}
      <section className="py-14 rq-greendark" id="contact">
        <div className="rq-container grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="rq-tag">{t.ctaSection.tag}</span>
            <h2 className="font-display mt-4 text-3xl md:text-4xl font-bold text-white leading-tight">{t.ctaSection.title}</h2>
            <p className="mt-6 text-[color:var(--rq-muted)] leading-relaxed">{t.ctaSection.desc}</p>
            <a href="tel:+971569818938" className="mt-6 inline-flex items-center gap-2 text-lg font-semibold text-[color:var(--rq-green)]" dir="ltr">
              <Phone size={20} /> +971 56 981 8938
            </a>
            <div className="mt-8 space-y-4">
              {t.ctaSection.steps.map((s, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="grid place-items-center h-9 w-9 rounded-full bg-[rgba(46,230,110,0.15)] text-[color:var(--rq-green)] font-bold text-sm shrink-0">{i + 1}</div>
                  <span className="text-[color:var(--rq-muted)]">{s}</span>
                </div>
              ))}
            </div>
          </div>
          <ConsultForm />
        </div>
      </section>
    </div>
  );
}
